import type { ChartType, Plugin } from 'chart.js';

import { wrapLabelLines } from './wsReportResultDistribution.utils';

// chart.js module augmentation: カスタムプラグインオプションを型に追加
declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    stackedBarSegmentLabels?: {
      labels: string[];
      line2Strings: string[];
      percentages: number[];
    };
  }
}

export const stackedBarSegmentLabelsPlugin: Plugin<'bar'> = {
  id: 'stackedBarSegmentLabels',
  afterDraw(chart) {
    const opts = chart.options.plugins?.stackedBarSegmentLabels;
    const labels: string[] = opts?.labels ?? [];
    const line2Strings: string[] = opts?.line2Strings ?? [];
    const percentages: number[] = opts?.percentages ?? [];
    if (!labels.length || labels.length !== line2Strings.length) return;

    const ctx = chart.ctx;
    const segmentCount = chart.data.datasets.length;
    const minSegmentWidthPx = 72;

    for (let i = 0; i < segmentCount; i++) {
      const meta = chart.getDatasetMeta(i);
      const el = meta?.data?.[0] as unknown as { x: number; y: number; base: number } | undefined;
      if (!el) continue;

      const left = Math.min(el.base, el.x);
      const right = Math.max(el.base, el.x);
      const segmentWidth = right - left;
      const centerX = (left + right) / 2;
      const centerY = el.y;

      ctx.save();
      ctx.fillStyle = '#1a1a1a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (segmentWidth < minSegmentWidthPx) {
        const pct = percentages[i] ?? 0;
        ctx.font = 'bold 11px sans-serif';
        ctx.fillText(`${pct}%`, centerX, centerY);
        ctx.restore();
        continue;
      }

      const label = labels[i] ?? '';
      const line2 = line2Strings[i] ?? '';

      const labelFont = '11px sans-serif';
      const lineHeight = 13;
      const maxWidth = Math.max(segmentWidth - 12, 32);

      // measure クロージャに渡す前に必ず labelFont を設定しておく（フォント未設定だと幅測定がずれる）
      ctx.font = labelFont;
      const labelLines = wrapLabelLines(label, maxWidth, s => ctx.measureText(s).width, {
        maxLines: 3,
      });

      const labelBlockHeight = labelLines.length * lineHeight;
      const totalHeight = labelBlockHeight + 6 + lineHeight;
      let y = centerY - totalHeight / 2 + lineHeight / 2;

      ctx.font = labelFont;
      for (const line of labelLines) {
        ctx.fillText(line, centerX, y);
        y += lineHeight;
      }

      y += 6;
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText(line2, centerX, y);
      ctx.restore();
    }
  },
};
